// Country helpers for the phone-auth country picker.
// Uses libphonenumber-js metadata + native Intl.DisplayNames for names,
// zero extra dependencies. Flag emojis are generated from the ISO code
// via Unicode regional-indicator code points.
import { getCountries, getCountryCallingCode } from 'libphonenumber-js/min';

// English-name fallback for runtimes where Intl.DisplayNames' region
// catalog is missing (some minimal ICU builds, headless test browsers).
// Only used when the browser returns an unusable value.
export const ENGLISH_REGION_NAMES = {
  AF: 'Afghanistan', AL: 'Albania', DZ: 'Algeria', AS: 'American Samoa',
  AD: 'Andorra', AO: 'Angola', AI: 'Anguilla', AG: 'Antigua & Barbuda',
  AR: 'Argentina', AM: 'Armenia', AW: 'Aruba', AU: 'Australia',
  AT: 'Austria', AZ: 'Azerbaijan', BS: 'Bahamas', BH: 'Bahrain',
  BD: 'Bangladesh', BB: 'Barbados', BY: 'Belarus', BE: 'Belgium',
  BZ: 'Belize', BJ: 'Benin', BM: 'Bermuda', BT: 'Bhutan',
  BO: 'Bolivia', BA: 'Bosnia & Herzegovina', BW: 'Botswana', BR: 'Brazil',
  IO: 'British Indian Ocean Territory', VG: 'British Virgin Islands',
  BN: 'Brunei', BG: 'Bulgaria', BF: 'Burkina Faso', BI: 'Burundi',
  KH: 'Cambodia', CM: 'Cameroon', CA: 'Canada', CV: 'Cape Verde',
  KY: 'Cayman Islands', CF: 'Central African Republic', TD: 'Chad',
  CL: 'Chile', CN: 'China', CX: 'Christmas Island', CC: 'Cocos Islands',
  CO: 'Colombia', KM: 'Comoros', CG: 'Congo - Brazzaville', CD: 'Congo - Kinshasa',
  CK: 'Cook Islands', CR: 'Costa Rica', CI: 'Côte d\u2019Ivoire', HR: 'Croatia',
  CU: 'Cuba', CW: 'Curaçao', CY: 'Cyprus', CZ: 'Czechia',
  DK: 'Denmark', DJ: 'Djibouti', DM: 'Dominica', DO: 'Dominican Republic',
  EC: 'Ecuador', EG: 'Egypt', SV: 'El Salvador', GQ: 'Equatorial Guinea',
  ER: 'Eritrea', EE: 'Estonia', SZ: 'Eswatini', ET: 'Ethiopia',
  FK: 'Falkland Islands', FO: 'Faroe Islands', FJ: 'Fiji', FI: 'Finland',
  FR: 'France', GF: 'French Guiana', PF: 'French Polynesia', GA: 'Gabon',
  GM: 'Gambia', GE: 'Georgia', DE: 'Germany', GH: 'Ghana',
  GI: 'Gibraltar', GR: 'Greece', GL: 'Greenland', GD: 'Grenada',
  GP: 'Guadeloupe', GU: 'Guam', GT: 'Guatemala', GG: 'Guernsey',
  GN: 'Guinea', GW: 'Guinea-Bissau', GY: 'Guyana', HT: 'Haiti',
  HN: 'Honduras', HK: 'Hong Kong SAR China', HU: 'Hungary', IS: 'Iceland',
  IN: 'India', ID: 'Indonesia', IR: 'Iran', IQ: 'Iraq',
  IE: 'Ireland', IM: 'Isle of Man', IL: 'Israel', IT: 'Italy',
  JM: 'Jamaica', JP: 'Japan', JE: 'Jersey', JO: 'Jordan',
  KZ: 'Kazakhstan', KE: 'Kenya', KI: 'Kiribati', XK: 'Kosovo',
  KW: 'Kuwait', KG: 'Kyrgyzstan', LA: 'Laos', LV: 'Latvia',
  LB: 'Lebanon', LS: 'Lesotho', LR: 'Liberia', LY: 'Libya',
  LI: 'Liechtenstein', LT: 'Lithuania', LU: 'Luxembourg', MO: 'Macao SAR China',
  MG: 'Madagascar', MW: 'Malawi', MY: 'Malaysia', MV: 'Maldives',
  ML: 'Mali', MT: 'Malta', MH: 'Marshall Islands', MQ: 'Martinique',
  MR: 'Mauritania', MU: 'Mauritius', YT: 'Mayotte', MX: 'Mexico',
  FM: 'Micronesia', MD: 'Moldova', MC: 'Monaco', MN: 'Mongolia',
  ME: 'Montenegro', MS: 'Montserrat', MA: 'Morocco', MZ: 'Mozambique',
  MM: 'Myanmar (Burma)', NA: 'Namibia', NR: 'Nauru', NP: 'Nepal',
  NL: 'Netherlands', NC: 'New Caledonia', NZ: 'New Zealand', NI: 'Nicaragua',
  NE: 'Niger', NG: 'Nigeria', NU: 'Niue', NF: 'Norfolk Island',
  KP: 'North Korea', MK: 'North Macedonia', MP: 'Northern Mariana Islands',
  NO: 'Norway', OM: 'Oman', PK: 'Pakistan', PW: 'Palau',
  PS: 'Palestinian Territories', PA: 'Panama', PG: 'Papua New Guinea',
  PY: 'Paraguay', PE: 'Peru', PH: 'Philippines', PL: 'Poland',
  PT: 'Portugal', PR: 'Puerto Rico', QA: 'Qatar', RE: 'Réunion',
  RO: 'Romania', RU: 'Russia', RW: 'Rwanda', WS: 'Samoa',
  SM: 'San Marino', ST: 'São Tomé & Príncipe', SA: 'Saudi Arabia', SN: 'Senegal',
  RS: 'Serbia', SC: 'Seychelles', SL: 'Sierra Leone', SG: 'Singapore',
  SX: 'Sint Maarten', SK: 'Slovakia', SI: 'Slovenia', SB: 'Solomon Islands',
  SO: 'Somalia', ZA: 'South Africa', KR: 'South Korea', SS: 'South Sudan',
  ES: 'Spain', LK: 'Sri Lanka', BL: 'St. Barthélemy', SH: 'St. Helena',
  KN: 'St. Kitts & Nevis', LC: 'St. Lucia', MF: 'St. Martin', PM: 'St. Pierre & Miquelon',
  VC: 'St. Vincent & Grenadines', SD: 'Sudan', SR: 'Suriname', SJ: 'Svalbard & Jan Mayen',
  SE: 'Sweden', CH: 'Switzerland', SY: 'Syria', TW: 'Taiwan',
  TJ: 'Tajikistan', TZ: 'Tanzania', TH: 'Thailand', TL: 'Timor-Leste',
  TG: 'Togo', TK: 'Tokelau', TO: 'Tonga', TT: 'Trinidad & Tobago',
  TN: 'Tunisia', TR: 'Turkey', TM: 'Turkmenistan', TC: 'Turks & Caicos Islands',
  TV: 'Tuvalu', UM: 'U.S. Outlying Islands', VI: 'U.S. Virgin Islands',
  UG: 'Uganda', UA: 'Ukraine', AE: 'United Arab Emirates', GB: 'United Kingdom',
  US: 'United States', UY: 'Uruguay', UZ: 'Uzbekistan', VU: 'Vanuatu',
  VA: 'Vatican City', VE: 'Venezuela', VN: 'Vietnam', WF: 'Wallis & Futuna',
  EH: 'Western Sahara', YE: 'Yemen', ZM: 'Zambia', ZW: 'Zimbabwe',
  AX: '\u00c5land Islands', AC: 'Ascension Island', TA: 'Tristan da Cunha',
};

// Small "popular" list floats to the top of the picker for common markets.
export const POPULAR_COUNTRIES = ['US', 'CA', 'GB', 'AU', 'IN', 'MX', 'BR', 'DE', 'FR', 'ES'];

/**
 * Emoji flag from a 2-letter ISO country code (e.g. 'US' → 🇺🇸).
 * Uses Unicode regional-indicator symbols A (U+1F1E6) … Z (U+1F1FF).
 */
export function flagEmoji(iso2) {
  if (!iso2 || iso2.length !== 2) return '🏳️';
  const A = 0x1f1e6;
  const codePoints = [
    A + iso2.toUpperCase().charCodeAt(0) - 65,
    A + iso2.toUpperCase().charCodeAt(1) - 65,
  ];
  return String.fromCodePoint(...codePoints);
}

let _regionNames = null;
function getRegionNames() {
  if (_regionNames) return _regionNames;
  const locales = [];
  try { if (typeof navigator !== 'undefined' && navigator.language) locales.push(navigator.language); } catch { /* noop */ }
  locales.push('en');
  try {
    const dn = new Intl.DisplayNames(locales, { type: 'region' });
    _regionNames = {
      of: (code) => {
        try {
          const name = dn.of(code);
          // Some runtimes return the input code back if the region catalog
          // is missing (e.g. old Chromium or minimal ICU). Guard against
          // that so we always show a real English name.
          return name && name !== code ? name : ENGLISH_REGION_NAMES[code] || code;
        } catch {
          return ENGLISH_REGION_NAMES[code] || code;
        }
      },
    };
  } catch {
    _regionNames = { of: (code) => ENGLISH_REGION_NAMES[code] || code };
  }
  return _regionNames;
}

/**
 * All countries libphonenumber-js knows about, sorted with popular ones
 * on top and the rest alphabetized by localized display name.
 */
export function allCountries() {
  const names = getRegionNames();
  const all = getCountries().map((iso2) => ({
    iso2,
    name: names.of(iso2) || iso2,
    dialCode: `+${getCountryCallingCode(iso2)}`,
    flag: flagEmoji(iso2),
  }));

  const popularSet = new Set(POPULAR_COUNTRIES);
  const popular = POPULAR_COUNTRIES
    .map((iso) => all.find((c) => c.iso2 === iso))
    .filter(Boolean);
  const rest = all
    .filter((c) => !popularSet.has(c.iso2))
    .sort((a, b) => a.name.localeCompare(b.name));
  return { popular, rest, byIso: Object.fromEntries(all.map((c) => [c.iso2, c])) };
}

/**
 * Best-effort detection of the user's country from browser locale.
 * navigator.language → 'en-US' → 'US'. Falls back to 'US' if unknown.
 */
export function guessDefaultCountry() {
  try {
    const loc = navigator.language || 'en-US';
    const parts = loc.split('-');
    const iso2 = (parts[1] || '').toUpperCase();
    if (iso2 && iso2.length === 2 && getCountries().includes(iso2)) return iso2;
  } catch { /* noop */ }
  return 'US';
}
